/**
 * Geräte-Cache für KI-Ergebnisse, adressiert per ICAO-Hex — die zweite von drei
 * Cache-Ebenen (Speicher → IndexedDB (hier) → DynamoDB im Backend). Damit lädt
 * ein Dossier bzw. Screening-Urteil beim erneuten Antippen desselben Flugzeugs
 * SOFORT, auch nach App-Neustart, ohne erneuten Netz-/Modellaufruf.
 *
 * Ein Datensatz pro Flugzeug: { hex, dossier, screen, updatedAt }.
 * Fällt IndexedDB aus (Privatmodus o. Ä.), bleibt nur der In-Memory-Cache — die
 * App funktioniert weiter, nur ohne geräteseitige Persistenz.
 */

const DB_NAME = 'flyradar-ai';
const STORE = 'ai';
const VERSION = 1;

let dbPromise = null;
const mem = new Map(); // hex -> { dossier, screen }

const norm = (hex) => (hex || '').trim().toLowerCase();

function openDb() {
  if (dbPromise) return dbPromise;
  if (typeof indexedDB === 'undefined') {
    dbPromise = Promise.resolve(null);
    return dbPromise;
  }
  dbPromise = new Promise((resolve) => {
    let req;
    try {
      req = indexedDB.open(DB_NAME, VERSION);
    } catch {
      resolve(null);
      return;
    }
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'hex' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
  return dbPromise;
}

function idbGet(hex) {
  return openDb().then((db) => {
    if (!db) return null;
    return new Promise((resolve) => {
      let tx;
      try {
        tx = db.transaction(STORE, 'readonly');
      } catch {
        resolve(null);
        return;
      }
      const req = tx.objectStore(STORE).get(hex);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  });
}

function idbPut(record) {
  return openDb().then((db) => {
    if (!db) return;
    return new Promise((resolve) => {
      let tx;
      try {
        tx = db.transaction(STORE, 'readwrite');
      } catch {
        resolve();
        return;
      }
      tx.objectStore(STORE).put(record);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    });
  });
}

async function read(hex) {
  const key = norm(hex);
  if (!key) return { dossier: null, screen: null };
  if (mem.has(key)) return mem.get(key);
  const rec = await idbGet(key);
  const val = { dossier: rec?.dossier ?? null, screen: rec?.screen ?? null };
  mem.set(key, val);
  return val;
}

async function write(hex, patch) {
  const key = norm(hex);
  if (!key) return;
  const cur = await read(key);
  const next = { ...cur, ...patch };
  mem.set(key, next);
  await idbPut({ hex: key, ...next, updatedAt: Date.now() });
}

export async function getDossier(hex) {
  return (await read(hex)).dossier;
}
export async function setDossier(hex, dossier) {
  return write(hex, { dossier });
}
export async function getScreen(hex) {
  return (await read(hex)).screen;
}
export async function setScreen(hex, screen) {
  return write(hex, { screen });
}
